import React from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import { apiSettingsCardSx } from '../../../constants/apiSettingsCardTypography';
import { useGetBusinessProfilesHandler } from '../../../dataHandlers/getBusinessProfilesHandler';
import { useGetConnectorsHandler } from '../../connectors/dataHandlers/getConnectorsHandler';
import { useGetSftpSettingsHandler, useSaveSftpReceivedSettingsHandler } from '../dataHandlers/sftpSettingsHandlers';

const RECEIVED_BUCKETS = [
  { key: 'INV', documentType: 'marmin.purchase.invoice', label: 'Commercial invoice (INV)' },
  { key: 'CN', documentType: 'marmin.purchase.credit-note', label: 'Self-billed CN (CN)' },
  { key: 'SBINV', documentType: 'marmin.sale.invoice', label: 'Self-billed invoice (SBINV)' },
  { key: 'SBCN', documentType: 'marmin.sale.credit-note', label: 'Commercial CN (SBCN)' },
];

const emptyRow = () => ({
  tin: '',
  file_format: 'json',
  connectors: { INV: '', CN: '', SBINV: '', SBCN: '' },
});

const profileLabel = (profile) => {
  const name = profile?.name || '';
  const tin = profile?.tin || '';
  if (name && tin) {
    return `${name} (${tin})`;
  }

  return tin || name || '';
};

const SftpReceivedSection = () => {
  const dispatch = useDispatch();
  const { received: savedReceived, isLoading: settingsLoading } = useGetSftpSettingsHandler();
  const { profiles, isLoading: profilesLoading } = useGetBusinessProfilesHandler({ searchQuery: {}, enabled: true });
  const { connectors, isLoading: connectorsLoading } = useGetConnectorsHandler();
  const { onSubmit, isPending } = useSaveSftpReceivedSettingsHandler();

  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    if (settingsLoading) {
      return;
    }
    setRows(
      savedReceived.length > 0
        ? savedReceived.map((row) => ({
            tin: row.tin ?? '',
            file_format: row.file_format ?? 'json',
            connectors: { ...emptyRow().connectors, ...(row.connectors ?? {}) },
          }))
        : [],
    );
  }, [savedReceived, settingsLoading]);

  const profilesWithTin = React.useMemo(
    () => profiles.filter((p) => String(p.tin || '').trim().length > 0),
    [profiles],
  );

  const connectorOptions = React.useMemo(() => connectors ?? [], [connectors]);

  const rowKeys = React.useMemo(
    () => new Set(rows.map((r) => `${r.tin}|${r.file_format}`).filter((k) => !k.startsWith('|'))),
    [rows],
  );

  const connectorsForBucket = (bucketKey) =>
    connectorOptions.filter((c) => c.document_type === RECEIVED_BUCKETS.find((b) => b.key === bucketKey)?.documentType);

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateConnector = (index, bucketKey, connectorKey) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, connectors: { ...row.connectors, [bucketKey]: connectorKey } } : row,
      ),
    );
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRows = () => {
    const keys = new Set();
    for (const row of rows) {
      if (!row.tin) {
        return 'Each row needs a routing TIN (business profile).';
      }
      if (!row.file_format) {
        return 'Each row needs a file format (JSON or XLSX).';
      }
      const key = `${row.tin}|${row.file_format}`;
      if (keys.has(key)) {
        return 'Each TIN and file format combination can only appear once.';
      }
      keys.add(key);
    }

    return null;
  };

  const handleSave = () => {
    const validationError = validateRows();
    if (validationError) {
      dispatch(showSnackbar({ message: validationError, severity: 'error' }));
      return;
    }

    onSubmit(
      rows.map((row) => {
        const connectors = {};
        RECEIVED_BUCKETS.forEach((bucket) => {
          const key = String(row.connectors?.[bucket.key] || '').trim();
          if (key) {
            connectors[bucket.key] = key;
          }
        });

        return {
          tin: row.tin.trim(),
          file_format: row.file_format,
          connectors,
        };
      }),
    );
  };

  const validationError = validateRows();
  const isBusy = settingsLoading || profilesLoading || connectorsLoading || isPending;

  return (
    <Stack spacing={1.5} sx={{ mt: 3 }}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <InboxIcon fontSize="small" color="primary" />
          <Typography sx={apiSettingsCardSx.title}>Received document connector mapping</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={apiSettingsCardSx.description}>
          Map routing TINs from received Peppol documents to an inbox file format and optional connector templates per
          document type (<code>invoice_type_code</code> / <code>credit_note_type_code</code>). Without a connector,
          documents are written to the received archive and index as Marmin JSON or the default bulk-upload XLSX (one
          row per line item). With a connector, ERP-shaped files are dropped in <code>received_inbox</code>.
        </Typography>
      </Box>

      {rows.length === 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No received mappings yet. Add a row per TIN and inbox file format.
          </Typography>
        </Box>
      )}

      <Stack spacing={1.5}>
        {rows.map((row, index) => {
          const profileValue = profilesWithTin.find((p) => p.tin === row.tin) ?? null;

          return (
            <Box key={`received-row-${index}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Autocomplete
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  options={profilesWithTin}
                  loading={profilesLoading}
                  disabled={isBusy}
                  value={profileValue}
                  getOptionLabel={profileLabel}
                  isOptionEqualToValue={(a, b) => a?.profile_id === b?.profile_id}
                  getOptionDisabled={(option) =>
                    rowKeys.has(`${option.tin}|${row.file_format}`) && option.tin !== row.tin
                  }
                  onChange={(_, profile) => updateRow(index, { tin: profile?.tin ?? '' })}
                  renderInput={(params) => <TextField {...params} label="TIN (profile)" placeholder="Select profile" />}
                />
                <FormControl size="small" sx={{ minWidth: 160, flexShrink: 0 }}>
                  <InputLabel>Format</InputLabel>
                  <Select
                    label="Format"
                    value={row.file_format}
                    disabled={isBusy}
                    onChange={(e) => updateRow(index, { file_format: e.target.value })}
                  >
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="xlsx">XLSX</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  aria-label="Remove received row"
                  disabled={isBusy}
                  onClick={() => removeRow(index)}
                  sx={{ flexShrink: 0 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                {RECEIVED_BUCKETS.map((bucket) => {
                  const options = connectorsForBucket(bucket.key);
                  const connectorValue = options.find((c) => c.key === row.connectors?.[bucket.key]) ?? null;

                  return (
                    <Box key={`${index}-${bucket.key}`} sx={{ flex: '1 1 220px', minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                        {bucket.label}
                      </Typography>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={options}
                        loading={connectorsLoading}
                        disabled={isBusy}
                        value={connectorValue}
                        getOptionLabel={(c) => c.name || c.key}
                        isOptionEqualToValue={(a, b) => a?.key === b?.key}
                        onChange={(_, connector) => updateConnector(index, bucket.key, connector?.key ?? '')}
                        renderInput={(params) => <TextField {...params} placeholder="Optional" />}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={isBusy}>
          Add mapping
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={isBusy || (rows.length > 0 && Boolean(validationError))}
        >
          Save received connector mapping
        </Button>
        {rows.length > 0 && validationError && (
          <Typography variant="caption" color="error">
            {validationError}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default SftpReceivedSection;
